import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import LeaveDetailsModal from "../modal/LeaveDetailsModal";
import PaginatedTable from "../library/Pagination";
import axios from "axios";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../config/api";
import Loader from "../../components/common/Loader";

const LeaveRequests = ({ }) => {
  const { token } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const statusOrder = { Requested: 1, Approved: 2, Rejected: 3, Cancelled: 4 };
  const [generalMaster, setGeneralMaster] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    approved: 0,
    pending: 0,
    rejected: 0,
  });

  const [filters, setFilters] = useState({
    start: "",
    end: "",
    type: "",
    lstatus: "",
  });

  const [notification, setNotification] = useState(null);

  useEffect(() => {

    const fetchgeneralMaster = async () => {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/Config/AllGeneralMaster`, {

      });
      if (res.ok) {
        const data = await res.json();
        setGeneralMaster(data);
      }
      setLoading(false);
    };
    fetchgeneralMaster();

  }, []);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async (customFilters = filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (customFilters.start?.trim()) params.append("start", customFilters.start);
      if (customFilters.end?.trim()) params.append("end", customFilters.end);
      if (customFilters.type) params.append("type", customFilters.type);
      if (customFilters.lstatus) params.append("status", customFilters.lstatus);

      const query = params.toString();
      const url = `${API_BASE_URL}/api/RequestTimeOff/requests${query ? `?${query}` : ""}`;

      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const sortedRequests = resp.data.requests.sort((a, b) => {
        const statusDiff = statusOrder[a.status] - statusOrder[b.status];
        if (statusDiff !== 0) return statusDiff;

        return new Date(b.startDate) - new Date(a.startDate);
      });
      setRequests(sortedRequests);
      setSummary(resp.data.summary);
    } catch (err) {
      setNotification({
        type: "error",
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (leave) => {
    const loggedInUserId = sessionStorage.getItem("username");
    const role = sessionStorage.getItem("userrole");

    setSelectedLeave({
      ...leave,
      isRequester: leave.employeeId === loggedInUserId,
      isAdmin: role === "1",
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedLeave(null);
    setIsModalOpen(false);
  };

  useEffect(() => {
    let result = [...requests];

    // Filter by search text
    if (searchText.trim() !== "") {
      const text = searchText.toLowerCase();
      result = result.filter(
        (req) =>
          req.employeeName?.toLowerCase().includes(text) ||
          req.employeeId?.toLowerCase().includes(text)
      );
    }

    // Optionally, keep existing filters
    if (filters.type) {
      result = result.filter((req) => req.leaveType === filters.type);
    }
    if (filters.lstatus) {
      result = result.filter((req) => req.status === filters.lstatus);
    }
    if (filters.start) {
      const startDate = new Date(filters.start);
      result = result.filter((req) => new Date(req.startDate) >= startDate);
    }
    if (filters.end) {
      const endDate = new Date(filters.end);
      result = result.filter((req) => new Date(req.endDate) <= endDate);
    }

    setFilteredRequests(result);
  }, [searchText, requests, filters]);

  return (
    <>
      {loading ? (
        <Loader message="Loading..." />
      ) : (
        <div className="flex flex-1 justify-center py-5">
          <div className="flex flex-col w-100 px-5 flex-1">
            {notification && (
              <div
                className={`mb-4 px-4 py-3 rounded flex justify-between items-start ${notification.type === "error"
                  ? "bg-red-100 text-red-700"
                  : "bg-green-100 text-green-700"
                  }`}
              >
                <span>{notification.message}</span>
                <button
                  onClick={() => setNotification(null)}
                  className="ml-4 font-bold text-lg"
                >
                  ×
                </button>
              </div>
            )}

            <div className="flex flex-wrap justify-between gap-3 mb-5">
              <h1 className="text-3xl font-bold text-[#121516]">Time Off Requests</h1>
            </div>


            <Filters
              searchText={searchText}
              setSearchText={setSearchText}
              filters={filters}
              setFilters={setFilters}
              onSearch={fetchRequests}
              generalMaster={generalMaster}
            />


            <h3 className="text-[#121516] text-lg font-bold px-4 pt-4">Summary</h3>
            <div className="flex flex-wrap gap-4 p-4">
              <SummaryCard title="Approved" count={summary.approved} />
              <SummaryCard title="Pending" count={summary.pending} />
              <SummaryCard title="Rejected" count={summary.rejected} />
            </div>

            <div className="flex justify-between w-full px-4">
              <h3 className="text-[#121516] text-lg font-bold px-4 pt-4">
                Leave Requests
              </h3>
              <div className="relative max-w-[250px] w-full">
                {/* Search Icon */}
                <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </span>

                {/* Input */}
                <input
                  type="text"
                  placeholder="Search by name"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="rounded-md border h-14 pl-12 pr-3 w-full focus:ring-1 focus:ring-blue-400 outline-none"
                />

              </div>
            </div>

            <div className="overflow-x-auto px-4 py-3">
              <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
                <PaginatedTable
                  columns={[
                    "Employee",
                    "Type",
                    "Start Date",
                    "End Date",
                    "Duration",
                    "Status",
                    "Action",
                  ]}
                  data={filteredRequests}
                  pageSize={10}
                  renderRow={(leave) => (
                    <tr key={leave.id} className="border-t border-[#dde1e3]">
                      <td className="px-4 py-2">
                        <label className="font-bold">{leave.employeeId}</label>
                        <br /> {leave.employeeName}
                      </td>
                      <td className="px-4 py-2 text-[#677883]">
                        {leave.leaveType}
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        {new Date(leave.startDate).toLocaleDateString("en-GB")}
                      </td>
                      <td className="px-4 py-2">
                        {" "}
                        {new Date(leave.endDate).toLocaleDateString("en-GB")}
                      </td>
                      <td className="px-4 py-2">{leave.leaveDays} days</td>
                      <td className="px-4 py-2">
                        {" "}
                        {/* <span className="rounded-lg px-4 py-1 bg-[#f1f3f4] text-sm font-medium"> */}
                        <span
                          className={`rounded-lg px-4 py-1 text-sm font-medium
                          ${leave.status === "Approved" ? "bg-green-100 text-green-700" : ""}
                          ${leave.status === "Rejected" ? "bg-red-100 text-red-700" : ""}
                          ${leave.status === "Requested" ? "bg-yellow-100 text-yellow-700" : ""}
                          ${leave.status === "Cancelled" ? "bg-gray-200 text-gray-700" : ""}
                        `}
                        >
                          {leave.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => handleOpenModal(leave)}
                          className="flex min-w-[80px] max-w-[80px] cursor-pointer items-center justify-center hover:bg-black hover:text-white overflow-hidden rounded-full h-8 px-4 bg-[#f1f3f4] text-[#121516] text-sm font-medium leading-normal w-full"
                        >
                          View
                        </button>

                        {/* {leave.status === "Pending" && (
                      <>
                        <button
                          onClick={() => openComment(leave.id, "Approved")}
                          className="px-2 py-1 bg-green-200 mr-1 rounded"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openComment(leave.id, "Rejected")}
                          className="px-2 py-1 bg-red-200 rounded"
                        >
                          Reject
                        </button>
                      </>
                    )} */}
                      </td>
                    </tr>
                  )}
                />
              </div>
              {selectedLeave && (
                <LeaveDetailsModal
                  isOpen={isModalOpen}
                  onClose={handleCloseModal}
                  leave={selectedLeave}
                  onRefresh={fetchRequests}
                  isRequester={selectedLeave.isRequester}
                  isAdmin={selectedLeave.isAdmin}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

};

const Filters = ({ searchText, setSearchText, filters, setFilters, onSearch, generalMaster }) => (
  <div className="px-4 py-3">
    <div className="flex items-center gap-[30px]">

      <input
        type="date"
        name="start"
        value={filters.start}
        onChange={(e) => setFilters({ ...filters, start: e.target.value })}
        className="form-input rounded-xl border h-14 p-3 max-w-[250px]"
        placeholder="Start Date"
      />
      <input
        type="date"
        name="end"
        value={filters.end}
        onChange={(e) => setFilters({ ...filters, end: e.target.value })}
        className="form-input rounded-xl border h-14 p-3 max-w-[250px]"
        placeholder="End Date"
      />
      <select
        name="type"
        value={filters.type}
        onChange={(e) => setFilters({ ...filters, type: e.target.value })}
        className="form-input rounded-xl border h-14 p-3 max-w-[250px]"
      >
        <option value="">Select type</option>
        {generalMaster.filter(x => x.mainId == 46).map((opt) => (
          <option key={opt.id} value={opt.title}>{opt.title}</option>
        ))}
      </select>
      <select
        name="lstatus"
        value={filters.lstatus}
        onChange={(e) => setFilters({ ...filters, lstatus: e.target.value })}
        className="form-input rounded-xl border h-14 p-3 max-w-[250px]"
      >
        <option value="">Select status</option>
        {generalMaster.filter(x => x.mainId == 50).map((opt) => (
          <option key={opt.id} value={opt.title}>{opt.title}</option>
        ))}
      </select>

      <div className="flex justify-end gap-[10px]">

        <button
          onClick={() => {
            const clearedFilters = { start: "", end: "", type: "", lstatus: "" };
            setFilters(clearedFilters);
            onSearch(clearedFilters);
          }}
          className="cancel-btn"
        >
          Clear
        </button>

        <button
          onClick={() => onSearch(filters)}
          className="btn-black"
        >
          Search
        </button>
      </div>
    </div>


  </div>
);

const SummaryCard = ({ title, count }) => (
  <div className="min-w-[158px] flex-1 flex flex-col gap-2 rounded-lg p-6 border border-[#dde1e3]">
    <p className="text-[#121516] text-base font-medium">{title}</p>
    <p className="text-[#121516] text-2xl font-bold">{count}</p>
  </div>
);

export default LeaveRequests;
